import React from 'react';
import { Link } from 'react-router-dom';
import { Package, FileVideo, LineChart, ArrowRight } from 'lucide-react';
import Reveal from '@/components/Reveal';

const FEATURES = [
  {
    icon: Package,
    no: '01',
    title: 'Native Product Placement',
    body:
      'Integrating brands seamlessly into organic digital lifestyles — placements that read as authentic cultural moments rather than advertising.',
  },
  {
    icon: FileVideo,
    no: '02',
    title: 'High-Fidelity Asset Licensing',
    body:
      'Utilizing our digital production arm to build custom commercial creative campaigns, licensed for global multi-channel deployment.',
  },
  {
    icon: LineChart,
    no: '03',
    title: 'Data-Driven Performance',
    body:
      'Exploiting advanced tracking metrics to guarantee optimal engagement ratios across every market, surface, and campaign cycle.',
  },
];

export default function CorporateServices() {
  return (
    <section id="services" className="bg-[#F5F5F5] py-24 lg:py-32">
      <div className="max-w-[1320px] mx-auto px-6 lg:px-10">
        <Reveal className="max-w-2xl mb-14">
          <span className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#2563EB] mb-4 block">
            Corporate Sponsorships & Services
          </span>
          <h2
            className="font-display font-bold text-[#0B0B0B] tracking-tight leading-tight"
            style={{ fontSize: 'clamp(2rem, 4vw, 3.25rem)' }}
          >
            Commercial Deliverables
          </h2>
          <p className="mt-5 text-[#0B0B0B]/60 text-base lg:text-lg font-light leading-relaxed">
            Structured offerings for major brands seeking measurable cultural traction.
          </p>
        </Reveal>

        <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
          {FEATURES.map((f, i) => {
            const Icon = f.icon;
            return (
              <Reveal key={f.title} delay={i * 0.1}>
                <div className="group h-full rounded-lg bg-white border border-[#0B0B0B]/10 p-8 lg:p-9 hover:border-[#2563EB] hover:-translate-y-1 hover:shadow-[0_20px_60px_-30px_rgba(37,99,235,0.4)] transition-all duration-500">
                  <div className="flex items-start justify-between mb-7">
                    <div className="w-11 h-11 rounded-lg border border-[#0B0B0B]/15 flex items-center justify-center group-hover:border-[#2563EB] group-hover:bg-[#2563EB] transition-all duration-500">
                      <Icon className="w-5 h-5 text-[#0B0B0B] group-hover:text-white transition-colors duration-500" />
                    </div>
                    <span className="font-display font-bold text-2xl text-[#0B0B0B]/15">{f.no}</span>
                  </div>
                  <h3 className="font-display font-bold text-xl lg:text-2xl text-[#0B0B0B] tracking-tight mb-4">
                    {f.title}
                  </h3>
                  <p className="text-[#0B0B0B]/60 leading-relaxed font-light text-sm lg:text-base">{f.body}</p>
                </div>
              </Reveal>
            );
          })}
        </div>

        <Reveal delay={0.15}>
          <div
            id="partnerships"
            className="mt-8 lg:mt-10 rounded-lg bg-[#0B0B0B] p-10 lg:p-14 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8 relative overflow-hidden"
          >
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_right,rgba(37,99,235,0.22),transparent_60%)]" />
            <div className="relative max-w-2xl">
              <h3
                className="font-display font-bold text-white tracking-tight leading-tight"
                style={{ fontSize: 'clamp(1.6rem, 3vw, 2.5rem)' }}
              >
                Corporate Partnerships
              </h3>
              <p className="mt-4 text-white/60 font-light leading-relaxed">
                Long-term brand alignments with dedicated account architecture, quarterly performance
                reviews, and priority access to the full roster.
              </p>
            </div>
            <Link
              to="/pitch-a-brand"
              className="relative group inline-flex items-center justify-center gap-2 px-8 h-14 rounded-lg text-sm font-semibold bg-[#2563EB] text-white hover:bg-white hover:text-[#0B0B0B] transition-colors duration-300 shrink-0"
            >
              Pitch a Brand
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </Reveal>
      </div>
    </section>
  );
}